// These data structures represent:
//   - a cursor position, starting at the first menu
//   - the cursor position can be moved "into" a menu item
//   - to move "out of" a menu item, back into the menu, is defined
//     by the menu itself, e.g. move up to get out of the menu item
// They are shared because they come from the db and are used frontend+backend

export const DefaultMenusDefinitionChannelPrefix = "anonymous-";

export type MenuItemId = string;
export type MenuItemVersionId = string;
export type MenuId = string;

export const DirectionalControllerTypes = ["directionalGlyph", "directionalPrototype01", "directionalPrototype02", "none"] as const;
export type DirectionalControllerType = (typeof DirectionalControllerTypes)[number];

export type MenuState = {
  selectedIndex?: number;
  // "forward" activates some items, and some items
  // keep the control, channeling the streams to the selected item
  // and only remove when "back"
  isSelectionControlled?: boolean;
};

export type ProjectorPayload = {
  channel?: string | undefined;
  backgroundColor?: string;
};

export type MenuDefinitionGlobalOverrides = {
  centerHeaders?: boolean;
  debugJSMetaframe?: string;
  GlobalProjector?: ProjectorPayload;
};

export type MenuDefinition = {
  name?: string;
  // URL or hex color
  background?: string;
  // A remote superslides projector
  projector?: ProjectorPayload;
  // Default directional controller type for horizontal axis (all items in this menu)
  horizontalControllerType?: DirectionalControllerType;
  // Default directional controller type for vertical axis (all items in this menu)
  verticalControllerType?: DirectionalControllerType;
  // Default controller render options applied to every item in this menu.
  // Individual slides can override these via MenuItemDefinition.controller.
  controller?: Pick<
    Partial<ConnectOptions>,
    | "hideCamera"
    | "renderHandsColor"
    | "renderHandsOpacity"
    | "renderPoseColor"
    | "renderPoseOpacity"
  >;
};

export interface Menu {
  id: MenuId;
  version: string;
  // The following fields correspond to db table columns
  // (db:menu.definition)
  definition: MenuDefinition;
  // (db:menu.menu_items) the core: the menu items
  items: MenuItemId[];
  // helps us track the version of the menu items to fetch from the db
  itemVersions?: Record<MenuItemId, MenuItemVersionId>;
}

export const MenuItemTypes = ["url", "metapage", "markdown"] as const;

export type MenuItemActionType = (typeof MenuItemTypes)[number];

export const MenuItemTypesMap: Record<MenuItemActionType, MenuItemActionType> =
  {
    markdown: "markdown",
    url: "url",
    metapage: "metapage",
  };

export type AnyMenuItemActionType = MenuItemActionUrl | MenuItemActionMetapage;

export interface MenuItemMinimal {
  /** version is set as soon as the data is pulled from the db as it's not actually store in the blob */
  version: MenuItemVersionId;
  name?: string;
  type: MenuItemActionType;
  thumbnail?: string;
}

export interface MenuItemActionPayload extends MenuItemMinimal {
  value?: AnyMenuItemActionType;
}

export type SendPayload = {
  url: string;
  body?: any;
};

export type MenuItemActionBase = {
  // urls can be updated
  // URLs can be metapage.io URLs or any URL that will be converted to a metapage
  // and will have the MetapageController automatically added so you can control
  url: string;
  // URL or hex color
  background?: string;
};

export type MenuItemActionUrl = MenuItemActionBase & {
  send?: SendPayload;
};

export type MenuItemActionMetapage = MenuItemActionBase & {
  // definitions are baked in
  // I cannot type it here because it's shared between deno and
  // This is a definition of a metapage (not a URL)
  // browser typescript
  definition: any;
};

export interface NotionSource {
  // The content might be generated from a notion page/block
  link: string;
  // The content might be generated from a notion page
  // pageIndex: number;
  lastEditedTime: string;
}

export interface MenuItemDefinition extends MenuItemActionPayload {
  // id: MenuItemId;
  // name?: string;
  // If this exists, then forward will navigate to this Menu
  link?: {
    // go to this menu
    menu: string;
    // maybe go to this menu item
    index?: number;
  };

  notion?: NotionSource;
  // Optional projector (Url) for this specific slide
  projector?: ProjectorPayload;

  /** Rich-text (HTML) speaker notes for this slide, edited in the left panel. */
  speakerNotes?: string;

  backgroundColor?: string;

  controller?: {
    position?: { x: number; y: number };
    size?: { width: number; height: number };
    opacity?: number;
    isBehind?: boolean;
    hidden?: boolean;
    disableSegmentation?: boolean;
    // Per-item override for directional controller type on horizontal axis
    horizontalControllerType?: DirectionalControllerType;
    // Per-item override for directional controller type on vertical axis
    verticalControllerType?: DirectionalControllerType;
  } & Pick<
    Partial<ConnectOptions>,
    | "canvasVerticalAlignment"
    | "debugClench"
    | "debugMicrophone"
    | "emitRawHandData"
    | "emitRawPoseData"
    | "enableCamera"
    | "enablePhone"
    | "frameRate"
    | "hideCamera"
    | "microphoneDetection"
    | "renderGrabTwistControllers"
    | "renderHandsColor"
    | "renderHandsOpacity"
    | "renderHandsOverlay"
    | "renderPoseColor"
    | "renderPoseOpacity"
    | "renderPoseOverlay"
    | "resolution"
    | "rotationControl"
    | "rotationRendering"
    | "scalarControl"
    | "scalarRendering1Hand"
    | "scalarRendering1HandLeft"
    | "scalarRendering1HandRight"
    | "scalarRendering2Hands"
    | "scalarRenderingBar"
    | "segmentation"
    | "zoomControl"
    | "zoomControlRender"
  >;
}

export type MenuItemEntry = [
  MenuItemId | undefined,
  MenuItemDefinition | undefined,
];

export interface MenusDefinition {
  menus: Record<MenuId, Menu>;
  menuItems: Record<MenuItemId, MenuItemDefinition>;
}

export type CursorMenusDefinitionState = {
  selectedMenuId: MenuId;
  menus: Map<MenuId, MenuState>;
};

export type CursorMenuDefinitionState = {
  menuId: MenuId;
  state: MenuState;
  timestamp: number;
  // Optional: only present in GetMenuState response to initialize browser history
  history?: CursorMenuDefinitionState[];
  historyIndex?: number;
};

// export type MenuCursor = {
//   cursor: [string, number | undefined];
// };

// copied from AccelerometerToDirection.ts
// that one (not this) is the source of truth
// I don't yet where this should live, it's complicated
// So updated this one if you make changes in AccelerometerToDirection.ts
export type TapDirection =
  | "forward"
  | "back"
  | "left"
  | "right"
  | "up"
  | "down";

export type SuperslidesMenuRequest = {
  // TODO: rename to SetMenuState or change GetMenuState->GetMenuCursor
  SetMenuCursor?: CursorMenuDefinitionState;
  // Browser client has to ask for the menu state since it shows it
  GetMenuState?: Record<string, never>; // no arguments
  SetDirection?: {
    direction: TapDirection;
    timestamp: number;
  };
  // Help us debug some stuff
  DebugDumpDefinition?: boolean; // no arguments
  SetDebug?: boolean; // no arguments
};

// MenusDefinitionJson is now identical to MenusDefinition since we switched from Map to Record
export type MenusDefinitionJson = MenusDefinition;

export type SuperslidesMenuUpdate = {
  MenusDefinition?: MenusDefinitionJson;
  MenuDefinitionState?: CursorMenuDefinitionState;
  SlidesProjectorPayload?: MenuItemMinimal;
  WebsocketActivity?: {
    input?: number;
    output?: number;
    forwarding?: number;
  };
};

// Used when building a single menu config
export interface SingleMenu {
  menu: Menu;
  menuItems: Map<string, MenuItemDefinition>;
}

export type ConnectTransformScreenCoordinates = {
  controller: {
    width: number;
    height: number;
    top: number;
    left: number;
  };
  display: {
    width: number;
    height: number;
    top: number;
    left: number;
  };
};

export type MicrophoneControlOptions =
  | "fistInFrontOfFace"
  | "handsFree"
  | "none";

export type ConnectOptionsResolution = "low" | "medium" | "high";

export type ConnectOptions = {
  cameraFlipHorizontal?: boolean;
  cameraRotation?: "0" | "90" | "180" | "270";
  debug?: boolean;
  debugClench: boolean;
  debugMicrophone: boolean;
  microphoneDetection: undefined | MicrophoneControlOptions[];
  enableCamera: boolean;
  enablePhone: boolean;
  segmentation: boolean;
  zoomControl: boolean;
  zoomControlRender: boolean;
  scalarControl: boolean;
  scalarRenderingBar: boolean;
  scalarRendering1Hand: boolean;
  scalarRendering1HandLeft: boolean;
  scalarRendering1HandRight: boolean;
  scalarRendering2Hands: boolean;
  rotationControl: boolean;
  rotationRendering: boolean;
  resolution: ConnectOptionsResolution;
  frameRate: "15" | "30" | "60" | "max";
  canvasVerticalAlignment: "top" | "bottom" | "center";
  emitRawHandData: boolean;
  emitRawPoseData: boolean;
  // When true, the camera video output is not rendered (camera still runs).
  // Because nothing is drawn, segmentation is also skipped.
  hideCamera: boolean;
  renderHandsOverlay: boolean;
  // Opacity (0..1) of the hands overlay.
  renderHandsOpacity: number;
  // CSS color of the hands overlay.
  renderHandsColor: string;
  renderPoseOverlay: boolean;
  // Opacity (0..1) of the pose overlay.
  renderPoseOpacity: number;
  // CSS color of the pose overlay.
  renderPoseColor: string;
  renderGrabTwistControllers: boolean;
  // Jitter compensation: 0.0 = no smoothing, 1.0 = maximum smoothing
  // Higher values reduce jitter but increase latency
  jitterCompensation?: number;
  // Number of frames to keep using last known good value when data is missing
  // Helps handle temporary model failures or lighting issues
  jitterMissingDataTolerance?: number;
  // Grab-twist controller options
  // controller sends the left right direction to the server MenuModel
  // sometimes you want to disable this e.g. in editing mode
  grabTwistNavigationMode?: "controller" | "output";
  grabTwistLabelsHorizontal?: { start: string; end: string };
  grabTwistLabelsVertical?: { start: string; end: string };
  grabTwistPositionMode?: "static" | "dynamic";

  flickControllerOutput?: "controller" | "output" | "both";
  flickControllerEnabled?: boolean;
};

/**
 * Mediapipe Pose and Hands data
 */
export type MediapipeLandmark = {
  x: number;
  y: number;
  z: number;
  visibility?: number;
};
export type MediapipeHandResults = {
  landmarks?: MediapipeLandmark[][] | undefined;
  worldLandmarks?: MediapipeLandmark[][] | undefined;
  handednesses?:
    | {
        categoryName: string;
        displayName: string;
        index: number;
        score: number;
      }[][]
    | undefined;
  handedness?:
    | {
        categoryName: string;
        displayName: string;
        index: number;
        score: number;
      }[][]
    | undefined;
};

export type MediapipePoseResults = {
  landmarks?: MediapipeLandmark[][] | undefined;
  worldLandmarks?: MediapipeLandmark[][] | undefined;
  segmentationMasks?: any | undefined;
};

export type CanvasBoundsType = {
  canvasWidth: number;
  canvasHeight: number;
  // Rendered video bounds within the canvas element (accounting for object-fit: contain)
  renderedX: number;
  renderedY: number;
  renderedWidth: number;
  renderedHeight: number;
};

export type Rect = {
  x: number;
  y: number;
  width: number;
  height: number;
};

export type HitBox = Rect & {
  z?: number;
  depth?: number;
  velocity?: { x: number; y: number; z: number };
};
export type HandHitBoxes = [HitBox | undefined, HitBox | undefined];
export type ClenchData = {
  clench?: number;
  handHitBoxes?: undefined | HandHitBoxes;
  isClenchLeft?: boolean;
  isClenchRight?: boolean;
  meanClenchAngleRight?: number | undefined;
  meanClenchAngleLeft?: number | undefined;
  meanClenchAngleRightNormalized?: number | undefined;
  meanClenchAngleLeftNormalized?: number | undefined;
};

// Browser viewport coordinates for clench hit testing
export type HandHitBoxBrowser = {
  x: number; // viewport pixels
  y: number; // viewport pixels
  width: number; // pixels
  height: number; // pixels
};

export type ClenchDataBrowser = {
  handHitBoxes: [HandHitBoxBrowser | undefined, HandHitBoxBrowser | undefined];
  isClenchLeft: boolean;
  isClenchRight: boolean;
};

export const DirectionControllerInputName = "Control4WayDirection";
export type DirectionControllerPayload = {
  label: string;
  index: 0 | 1 | 2 | 3; // 0=left, 1=right, 2=forward, 3=back
  timestamp: number;
};





// export type Palms = [PalmData | undefined, PalmData | undefined];

// export type HandTokenizer = {
//   // @deprecated
//   clench?: ClenchData;
//   handhitBoxes?: HandHitBoxes;
//   pinchIndexFinger?: HandTokenActivePoint;
//   pinchIndexMiddleFingers?: HandTokenActivePoint;
//   pinchAllFingers?: HandTokenActivePoint;
//   palms: Palms;
// };

/**
 * Control data types for hands
 */

export type HandsControlData = {
  clench: ClenchData | undefined;
  wristRotationLeft: number | undefined;
  wristRotationRight: number | undefined;
  palmOrthogonalEulerNotClenched: number[] | undefined;
  palmOrthogonalQuaternionNotClenched: number[] | undefined;
  wristRotationNotClenched: number | undefined;
  handSplayNotClenched: number | undefined;
  isPalmFlatLeft: boolean | undefined;
  isPalmFlatRight: boolean | undefined;
};


// === Token types ===

export type HandTokenName =
  | "wholeHand" // contains also velocity vector and
  | "wristRotation"
  | "wristAngle"
  | "palmNormal" // palm normal vector
  | "handsVector" // directional vectors from hands, both left to right and right to left
  | "fist" // all fingers clenched
  | "openPalm" // all extended + high splay
  | "flatPalm" // all extended + low splay
  | "pointing" // index only extended
  | "peace" // index + middle (V / peace sign)
  | "threeUp" // index + middle + ring extended
  | "fourUp" // all four main fingers extended
  | "thumbUp" // thumb extended, fingers clenched
  | "pinch" // thumb and index tips close together
  | "ok"; // pinch + middle/ring/pinky extended

export type BimanualTokenName =
  | "bothFists" // both hands clenched
  | "bothOpen" // both hands open
  | "handsTogether"; // hand bounding boxes overlapping

// export type TokenData = {
//   hitbox?: HitBox | undefined;
//   centroid?: { x: number; y: number } | undefined;
//   angle?: number | undefined;
//   angularVelocity?: number | undefined;
//   normal?: { x: number; y: number; z: number } | undefined;
//   facing?: "palmar" | "dorsal" | undefined;
//   confidence?: number; // 0–1
//   meanClenchAngle?: number | undefined;
//   meanClenchAngleNormalized?: number | undefined;
//   isClench?: boolean | undefined;
// };

export type HandTokenHitbox = {
  hitbox?: HitBox | undefined;
  // centroid?: { x: number; y: number } | undefined;
  // angle?: number | undefined;
  // angularVelocity?: number | undefined;
  // normal?: { x: number; y: number; z: number } | undefined;
  // facing?: "palmar" | "dorsal" | undefined;
  // confidence?: number; // 0–1
  // meanClenchAngle?: number | undefined;
  // meanClenchAngleNormalized?: number | undefined;
  // isClench?: boolean | undefined;
};

export type HandTokenHitboxFist = {
  hitbox: HitBox;
  meanClenchAngle: number;
  meanClenchAngleNormalized: number;
};

export type HandTokenAngle = {
  // hitbox?: HitBox | undefined;
  // centroid?: { x: number; y: number } | undefined;
  angle?: number | undefined;
  angularVelocity?: number | undefined;
  normal?: { x: number; y: number; z: number } | undefined;
  // facing?: "palmar" | "dorsal" | undefined;
  // confidence?: number; // 0–1
  // meanClenchAngle?: number | undefined;
  // meanClenchAngleNormalized?: number | undefined;
  // isClench?: boolean | undefined;
};

export type HandTokenActivePoint = {
  duration?: number;
  centroid?: { x: number; y: number; z?: number };
  hitbox?: HitBox;
};

export type NormalOnly = {
  // hitBox?: HitBox;
  // wristRotation?: number;
  // wristAngularVelocity?: number;
  // palmNormal?: { x: number; y: number; z: number };
  normal?: { x: number; y: number; z: number } | undefined;
};

export type PalmData = NormalOnly & {
  // hitBox?: HitBox;
  // wristRotation?: number;
  // wristAngularVelocity?: number;
  // palmNormal?: { x: number; y: number; z: number };
  surfaceVisible?: "palmar" | "dorsal" | undefined;
};

export type HandTokenResult = HandTokenHitbox | HandTokenAngle | HandTokenActivePoint | undefined;
export type SingleHandTokenResult = {
  wholeHand?: HandTokenHitbox;
  palmNormal?: HandTokenAngle;
  wristRotation?: HandTokenAngle;
  wristAngle?: HandTokenAngle;
  /** Wrist velocity in world-space units/sec (approx meters/sec) */
  velocityWorld?: { x: number; y: number; z: number };
  /** Wrist velocity in screen-space normalized units/sec */
  velocityScreen?: { x: number; y: number; z: number };
  /** True when the hand has been below the speed threshold continuously for the settle duration */
  isSettled?: boolean;
  fist?: HandTokenHitboxFist;
  openPalm?: PalmData;
  flatPalm?: PalmData;
  pointing?: HandTokenActivePoint;
  peace?: HandTokenHitbox;
  threeUp?: HandTokenHitbox;
  fourUp?: HandTokenHitbox;
  thumbUp?: HandTokenHitbox;
  ok?: HandTokenHitbox;
  /** Aggregate: active when any pinch type is active; uses the most specific active pinch's data. */
  pinch?: HandTokenActivePoint;
  pinchIndexFinger?: HandTokenActivePoint;
  pinchIndexMiddleFingers?: HandTokenActivePoint;
  pinchAllFingers?: HandTokenActivePoint;
};

export type BothHandsTokenResult = {
  palmsNormal?: NormalOnly;
  
};

export type HandTokenizerResults = {
  left?: SingleHandTokenResult | undefined;
  right?: SingleHandTokenResult | undefined;
  both?: BothHandsTokenResult | undefined;
};

export type PoseWristRotation = {
  poseWristQuaternion: {
    w: number;
    x: number;
    y: number;
    z: number;
  };
  poseWristRoll: number;
  poseWristPitch: number;
  poseWristYaw: number;
  poseWristDistance: number;
};

export type PoseControlData = Partial<PoseWristRotation> | undefined;

export interface Point3D {
  x: number;
  y: number;
  z: number;
}

export interface Vector3D {
  x: number;
  y: number;
  z: number;
}

export interface EulerAngles {
  pitch: number;
  yaw: number;
  roll: number;
}

export interface TwoDOFAngles {
  pitch: number;
  yaw: number;
}

export interface OneDOFAngles {
  elbow_L_flexion: number;
  elbow_R_flexion: number;
  knee_L_flexion: number;
  knee_R_flexion: number;
}

export interface TwoDOFJoints {
  wrist_L_pitch: number;
  wrist_L_yaw: number;
  wrist_R_pitch: number;
  wrist_R_yaw: number;
  ankle_L_pitch: number;
  ankle_L_yaw: number;
  ankle_R_pitch: number;
  ankle_R_yaw: number;
}

export interface ThreeDOFJoints {
  shoulder_L_pitch: number;
  shoulder_L_yaw: number;
  shoulder_L_roll: number;
  shoulder_R_pitch: number;
  shoulder_R_yaw: number;
  shoulder_R_roll: number;
  hip_L_pitch: number;
  hip_L_yaw: number;
  hip_L_roll: number;
  hip_R_pitch: number;
  hip_R_yaw: number;
  hip_R_roll: number;
  neck_pitch: number;
  neck_yaw: number;
  neck_roll: number;
  spine_pitch: number;
  spine_yaw: number;
  spine_roll: number;
}



export interface PoseAngles {
  oneDOF: OneDOFAngles;
  twoDOF: TwoDOFJoints;
  threeDOF: ThreeDOFJoints;
}

/** Emitted on the combined results / metaframe output stream for pinch gestures */
export type PinchGestureStreamOutput = {
  event: "PinchDown" | "PinchMove" | "PinchUp";
  centroid?: { x: number; y: number; z?: number };
};

export type CombinedResults =
  & {
    timestamp?: number;
    handTokens?: HandTokenizerResults | undefined;
    PinchTwoFingerEvent?: PinchGestureStreamOutput;
    PinchAllFingersEvent?: PinchGestureStreamOutput;
    /** Per-hand map of active `HandTokenName` → token payload (flat outputs for bindings) */
    handTokenLeft?: Partial<Record<HandTokenName, unknown>>;
    handTokenRight?: Partial<Record<HandTokenName, unknown>>;
  }
  & Partial<HandsControlData>
  & Partial<PoseControlData>
  & { poseAngles?: PoseAngles | undefined }
  & {
    hands?: MediapipeHandResults | undefined;
  }
  & { pose?: MediapipePoseResults | undefined }
  & {
    canvasBounds?: CanvasBoundsType | undefined;
  };

